import * as angular from "angular";
import {ITimeoutService} from "angular";
import * as _ from "lodash";
import {SiteFiltersType} from "../../commons/types/site-filters.type";
import {JobsGroupByEnum} from "../types/jobs-group-by.enum";
import {escapeForJsonSearch} from "../../commons/utils/json-search-escape.util";

export interface PlanningJobsFilterContext {
  filterConfig: any;
  jobIdToUnassignedJobData?: any;
  selectedClusterId?: string;
  highlightedRouteIds?: Set<string>;
  highlightedLeftoverIds?: Set<string>;
}

export interface PlanningJobsSortContext {
  sortBy: string;
  sortByType: 'asc' | 'desc';
  autoMoveSelectionToTop: boolean;
  selectedIds: string[];
  routes: any[];
}

export class PlanningJobsHelperService {

  public siteFiltersType: typeof SiteFiltersType = SiteFiltersType;

  /* @ngInject */
  constructor(private $timeout: ITimeoutService, private UUIDService, private $filter) {}

  public initFilters(panel: any) {
    if (!panel.tabsFilterConfig) {
      panel.tabsFilterConfig = {};
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex]) {
      panel.tabsFilterConfig[panel.selectedTabIndex] = {};
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.agentSkills]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.agentSkills] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.serviceAreaIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.serviceAreaIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.tags]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.tags] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.resourceIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.resourceIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.branchIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.branchIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.depotIds]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.depotIds] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.leftoverConstrains]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.leftoverConstrains] = [];
    }
    if (!panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.labels]) {
      panel.tabsFilterConfig[panel.selectedTabIndex][this.siteFiltersType.labels] = [];
    }
  }

  public setLeftoverConstraints(draft: any): any[] {
    let leftoverConstrains = [];
    if (draft?.jobIdToUnassignedJobData) {
      _.forOwn(draft.jobIdToUnassignedJobData, (value, jobId) => {
        const constrains = _.flatMap(value.failedConstraints, 'constraint');
        leftoverConstrains = leftoverConstrains.concat(constrains);
      });
    }
    return _.uniqBy(leftoverConstrains, 'name');
  }

  public filterJobByFilters(job: any, ctx: PlanningJobsFilterContext): boolean {
    const shouldReturnByQuery: boolean = this.filterByQuery(job, ctx.filterConfig);
    const shouldReturnBySkill: boolean = this.filterBySkills(job, ctx.filterConfig);
    const shouldReturnByTags: boolean = this.filterByTags(job, ctx.filterConfig);
    const shouldReturnByAreaIds: boolean = this.filterByAreaIds(job, ctx.filterConfig);
    const shouldReturnByResourceIds: boolean = this.filterByResourceIds(job, ctx.filterConfig);
    const shouldReturnByInRoute: boolean = this.filterByInRoute(job, ctx.filterConfig);
    const shouldReturnByIsPacked: boolean = this.filterByIsPacked(job, ctx.filterConfig);
    const shouldReturnByIsSynced: boolean = this.filterByIsSynced(job, ctx.filterConfig);
    const shouldReturnByBranchIds: boolean = this.filterByBranchIds(job, ctx.filterConfig);
    const shouldReturnByDepotIds: boolean = this.filterByDepotIds(job, ctx.filterConfig);
    const shouldReturnByLabels: boolean = this.filterByLabels(job, ctx.filterConfig);
    const shouldReturnBySelectedLeftoverConstrains: boolean = this.filterBySelectedLeftoverConstrains(job, ctx.jobIdToUnassignedJobData, ctx.filterConfig);
    const shouldReturnBySelectedCluster: boolean = this.filterBySelectedCluster(job, ctx.selectedClusterId, ctx.highlightedRouteIds, ctx.highlightedLeftoverIds);

    return shouldReturnByQuery &&
      shouldReturnBySkill &&
      shouldReturnByTags &&
      shouldReturnByResourceIds &&
      shouldReturnByAreaIds &&
      shouldReturnByInRoute &&
      shouldReturnByIsPacked &&
      shouldReturnByIsSynced &&
      shouldReturnBySelectedLeftoverConstrains &&
      shouldReturnByBranchIds &&
      shouldReturnByDepotIds &&
      shouldReturnByLabels &&
      shouldReturnBySelectedCluster;
  }

  public filterGroupJobByFilters(groupJob: any, ctx: PlanningJobsFilterContext): boolean {
    if (groupJob.groupedByType) {
      return !!_.find(groupJob.jobs, (j) => {
        return this.filterJobByFilters(j, ctx);
      });
    } else {
      return this.filterJobByFilters(groupJob, ctx);
    }
  }

  public filterByQuery(job: any, filterConfig: any): boolean {
    return !filterConfig.queryString ? true : _.includes(JSON.stringify(job), escapeForJsonSearch(filterConfig.queryString));
  }

  public filterBySkills(job: any, filterConfig: any): boolean {
    const skills = filterConfig[this.siteFiltersType.agentSkills];
    return !_.get(skills, 'length') || _.filter(job.skills, (skill) => {
      return _.includes(skills, skill);
    }).length > 0;
  }

  public filterByTags(job: any, filterConfig: any): boolean {
    const tags = filterConfig[this.siteFiltersType.tags];
    const noTags = !!_.find(tags, (tag) => {
      return tag === 'noTag';
    });

    if (!_.get(job, 'tags.length')) {
      return _.get(tags, 'length', false) ? noTags : true;
    } else {
      return !_.get(tags, 'length') || _.filter(job.tags, (tag) => {
        return _.includes(tags, tag);
      }).length > 0;
    }
  }

  public filterByLabels(job: any, filterConfig: any): boolean {
    const labelsFilter = filterConfig[this.siteFiltersType.labels];
    const labels = _.uniq(_.compact(_.flatMap(job.packages, 'labels')));
    return !_.get(labelsFilter, 'length') || _.includes(labelsFilter, labels);
  }

  public filterByAreaIds(job: any, filterConfig: any): boolean {
    const areaIds = filterConfig[this.siteFiltersType.serviceAreaIds];
    return !_.get(areaIds, 'length') || _.filter(job.areaIds, (aid) => {
      return _.includes(areaIds, aid);
    }).length > 0;
  }

  public filterByBranchIds(job: any, filterConfig: any): boolean {
    const branchIds = filterConfig[this.siteFiltersType.branchIds];
    return !_.get(branchIds, 'length') || _.filter(job.branchIds, (aid) => {
      return _.includes(branchIds, aid);
    }).length > 0;
  }

  public filterByDepotIds(job: any, filterConfig: any): boolean {
    const depotIds = filterConfig[this.siteFiltersType.depotIds];
    return !_.get(depotIds, 'length') || _.filter(job.stops, (s) => {
      return _.includes(depotIds, s.depotId);
    }).length > 0;
  }

  public filterByResourceIds(job: any, filterConfig: any): boolean {
    const resourceIds = filterConfig[this.siteFiltersType.resourceIds];
    return !_.get(resourceIds, 'length') || _.includes(resourceIds, job.resourceId);
  }

  public filterByInRoute(job: any, filterConfig: any): boolean {
    const inRoute = filterConfig[this.siteFiltersType.inRoute];
    return _.isUndefined(inRoute) ||
      _.isEqual(job.routed, inRoute) ||
      (!!job.groupedByType && (!!inRoute ? !!job.routedCount : job.routedCount < job.jobIds?.length));
  }

  public filterByIsPacked(job: any, filterConfig: any): boolean {
    const isPacked = filterConfig[this.siteFiltersType.isPacked];
    return _.isUndefined(isPacked) || _.isEqual(job.packed, isPacked);
  }

  public filterByIsSynced(job: any, filterConfig: any): boolean {
    const isSynced = filterConfig[this.siteFiltersType.isSynced];
    return _.isUndefined(isSynced) || _.isEqual(!!job.lastSyncedFromStepAt, isSynced);
  }

  public filterBySelectedLeftoverConstrains(job: any, jobIdToUnassignedJobData: any, filterConfig: any): boolean {
    const leftoverConstraints = filterConfig[this.siteFiltersType.leftoverConstrains];
    if (!leftoverConstraints?.length) {
      return true;
    } else if (!jobIdToUnassignedJobData || !jobIdToUnassignedJobData[job.id]) {
      return false;
    } else {
      const jobConstrains = _.flatMap(jobIdToUnassignedJobData[job.id].failedConstraints, 'constraint');
      const jobConstrainsNames = _.map(jobConstrains, 'name');
      const selectedLeftoverConstrainsNames = _.map(leftoverConstraints, 'name');
      return _.intersection(selectedLeftoverConstrainsNames, jobConstrainsNames).length > 0;
    }
  }

  public filterBySelectedCluster(job: any, selectedClusterId: string, highlightedRouteIds: Set<string>, highlightedLeftoverIds: Set<string>): boolean {
    if (selectedClusterId) {
      if (job.routeId) {
        return highlightedRouteIds?.has(job.routeId);
      } else {
        return highlightedLeftoverIds?.has(job.id);
      }
    }
    return true;
  }

  public setJobsData(jobs: any[], routes: any[]) {
    _.forEach(jobs, (j) => {
      j.resourceId = undefined;
      j.spotsAmount = this.$filter('orderSpots')(j.packages);
      j.packagesAmount = this.$filter('servicePackagesCount')(j.packages);
      j.packagesWeight = _.reduce(j.packages, (sum, i) => {
        return sum + _.get(i, 'weight', 0);
      }, 0);
      j.packagesVolume = _.reduce(j.packages, (sum, i) => {
        return sum + _.get(i, 'volume', 0);
      }, 0);
      j.packagesPallets = _.reduce(j.packages, (sum, i) => {
        return sum + _.get(i, 'pallets', 0);
      }, 0);
      j.packagesValue = _.reduce(j.packages, (sum, i) => {
        return sum + _.get(i, 'value', 0);
      }, 0);

      j.city = !!j.jobType ? (j.jobType === 'Delivery' ? j.stops[1].address.city : j.stops[0].address.city) : undefined;

      let jobAddressQualityValue: number = undefined;
      j.stops?.forEach((s) => {
        const stopQualityGroup = _.get(s, 'address.addressResolvingInfo.qualityGroup');
        const stopAddressQualityValue = stopQualityGroup === 'High' ? 3 : (stopQualityGroup === 'Medium' ? 2 : 1);
        if (stopQualityGroup && (!jobAddressQualityValue || stopAddressQualityValue < jobAddressQualityValue)) {
          jobAddressQualityValue = stopAddressQualityValue;
        }
      });

      j.addressQuality = jobAddressQualityValue;

      if (!!j.groupedByType) {
        let isRouted = false;
        j.jobIds.forEach(jid => {
          const route = _.find(routes, function (r) {
            return _.includes(r.jobIds, jid);
          });
          if (!!route) {
            _.set(j, 'resourceId', true);
            _.set(j, 'locked', true);
            isRouted = true;
          }
        });
      } else {
        if (j.routed) {
          const route = _.find(routes, function (r) {
            return _.includes(r.jobIds, j.id);
          });
          if (route) {
            _.set(j, 'resourceId', route.resourceId);
            _.set(j, 'locked', route.locked);
          }
        }
      }
    });
  }

  public getSortedJobs(jobs: any[], ctx: PlanningJobsSortContext): any[] {
    if (!jobs) {
      return;
    }
    let routedJobs = [];
    let unRoutedJobs = [];
    let excludedJobs = [];

    _.forEach(jobs, (j) => {
      if (!!j.groupedByType) {
        let isRouted = false;
        j.jobIds.forEach(jid => {
          const route = _.find(ctx.routes, function (r) {
            return _.includes(r.jobIds, jid);
          });
          if (!!route) {
            isRouted = true;
          }
        });
        if (isRouted) {
          routedJobs.push(j);
        } else {
          unRoutedJobs.push(j);
        }
      } else {
        if (j.routed) {
          routedJobs.push(j);
        }
        if (!j.routed && !j.excluded) {
          unRoutedJobs.push(j);
        }
        if (j.excluded) {
          excludedJobs.push(j);
        }
      }
    });

    const unlockedJobs = _.filter(routedJobs, function (j) {
      return !j.locked;
    });
    const lockedJobs = _.filter(routedJobs, function (j) {
      return j.locked;
    });

    routedJobs = _.concat(unlockedJobs, lockedJobs);

    const sortedJobs = _.concat(routedJobs, unRoutedJobs, excludedJobs);

    if (!ctx.autoMoveSelectionToTop) {
      return _.orderBy(sortedJobs, [ctx.sortBy], [ctx.sortByType]);
    }

    const selectedJobs = _.filter(sortedJobs, (j) => {
      return j.groupedByType ?
        !!_.intersection(j.jobIds, ctx.selectedIds).length :
        _.includes(ctx.selectedIds, j.id);
    });
    const unselectedJobs = _.filter(sortedJobs, (j) => {
      return j.groupedByType ?
        !_.intersection(j.jobIds, ctx.selectedIds).length :
        !_.includes(ctx.selectedIds, j.id);
    });
    return _.concat(_.orderBy(selectedJobs, [ctx.sortBy], [ctx.sortByType]), _.orderBy(unselectedJobs, [ctx.sortBy], [ctx.sortByType]));
  }

  public setGroupJobsByType(jobs: any[], jobsGroupByType: JobsGroupByEnum, routes: any[]): any[] {
    let groupedJobs = [];

    if (jobsGroupByType === JobsGroupByEnum.account) {
      groupedJobs = this.groupJobsByAccount(jobs);
    }
    if (jobsGroupByType === JobsGroupByEnum.pickupLocation) {
      groupedJobs = this.groupJobsByPickupLocation(jobs);
    }
    if (jobsGroupByType === JobsGroupByEnum.dropoffLocation) {
      groupedJobs = this.groupJobsByDropoffLocation(jobs);
    }
    return groupedJobs;
  }

  public groupJobsByAccount(jobs: any[]): any[] {
    const groupedJobsByAccountMap = {};

    jobs.forEach(job => {
      if (!groupedJobsByAccountMap[job.accountId]) {
        groupedJobsByAccountMap[job.accountId] = {
          id: this.UUIDService.generate(),
          accountName: job.accountName,
          accountId: job.accountId,
          packages: [],
          jobIds: [],
          jobs: [],
          orderIds: [],
          groupedByType: JobsGroupByEnum.account,
          routedCount: !!job.routed ? 1 : 0,
          isDisableSelect: false
        };
      } else if (!!job.routed) {
        groupedJobsByAccountMap[job.accountId].routedCount++;
      }

      if (!!job.lockedRouteId) {
        groupedJobsByAccountMap[job.accountId].isDisableSelect = true;
      }

      const groupedJobsObj = groupedJobsByAccountMap[job.accountId];

      if (job.packages) {
        groupedJobsObj.packages = groupedJobsObj.packages.concat(job.packages);
      }

      groupedJobsObj.jobIds.push(job.id);
      groupedJobsObj.jobs.push(job);

      if (job.jobType === 'Operation') {
        if (job.stops[0].notBefore < groupedJobsObj.notBefore || !groupedJobsObj.notBefore) {
          groupedJobsObj.notBefore = job.stops[0].notBefore;
        }
        if (job.stops[0].notAfter > groupedJobsObj.notAfter || !groupedJobsObj.notAfter) {
          groupedJobsObj.notAfter = job.stops[0].notAfter;
        }
      } else {
        if (job.stops[0].notBefore < groupedJobsObj.notBefore || !groupedJobsObj.notBefore) {
          groupedJobsObj.notBefore = job.stops[0].notBefore;
        }
        if (job.stops[1].notAfter > groupedJobsObj.notAfter || !groupedJobsObj.notAfter) {
          groupedJobsObj.notAfter = job.stops[1].notAfter;
        }
      }
    });

    let groupedJobs = [];

    _.forOwn(groupedJobsByAccountMap, (value: any, key) => {
      if (value.jobIds.length > 1) {
        groupedJobs.push(value);
      } else {
        const job = jobs.find((j) => j.id === value.jobIds[0]);
        groupedJobs.push(job);
      }
    });

    return groupedJobs;
  }

  public groupJobsByPickupLocation(jobs: any[]): any[] {
    const groupedJobsByPickupLocationMap = {};
    const fieldServiceJobs = [];

    jobs.forEach(job => {
      if (job.jobType === 'Operation') {
        fieldServiceJobs.push(job);
        return;
      }

      const jobMapId = job.stops[0].address.location.lng.toString() + job.stops[0].address.location.lat.toString();

      if (!groupedJobsByPickupLocationMap[jobMapId]) {
        groupedJobsByPickupLocationMap[jobMapId] = {
          id: this.UUIDService.generate(),
          packages: [],
          jobIds: [],
          jobs: [],
          groupedByType: JobsGroupByEnum.pickupLocation,
          address: job.stops[0].address,
          routedCount: !!job.routed ? 1 : 0,
          isDisableSelect: false
        };
      } else if (!!job.routed) {
        groupedJobsByPickupLocationMap[jobMapId].routedCount++;
      }

      if (!!job.lockedRouteId) {
        groupedJobsByPickupLocationMap[jobMapId].isDisableSelect = true;
      }

      const groupedJobsObj = groupedJobsByPickupLocationMap[jobMapId];

      if (job.packages) {
        groupedJobsObj.packages = groupedJobsObj.packages.concat(job.packages);
      }

      groupedJobsObj.jobIds.push(job.id);
      groupedJobsObj.jobs.push(job);

      if (job.stops[0].notBefore < groupedJobsObj.notBefore || !groupedJobsObj.notBefore) {
        groupedJobsObj.notBefore = job.stops[0].notBefore;
      }

      if (job.stops[1].notAfter > groupedJobsObj.notAfter || !groupedJobsObj.notAfter) {
        groupedJobsObj.notAfter = job.stops[1].notAfter;
      }
    });

    let groupedJobs = [];

    _.forOwn(groupedJobsByPickupLocationMap, (value: any, key) => {
      if (value.jobIds.length > 1) {
        groupedJobs.push(value);
      } else {
        const job = jobs.find((j) => j.id === value.jobIds[0]);
        groupedJobs.push(job);
      }
    });

    groupedJobs = groupedJobs.concat(fieldServiceJobs);

    return groupedJobs;
  }

  public groupJobsByDropoffLocation(jobs: any[]): any[] {
    const groupedJobsByDropoffLocationMap = {};
    const fieldServiceJobs = [];

    jobs.forEach(job => {
      if (job.jobType === 'Operation') {
        fieldServiceJobs.push(job);
        return;
      }

      const jobMapId = job.stops[1].address.location.lng.toString() + job.stops[1].address.location.lat.toString();

      if (!groupedJobsByDropoffLocationMap[jobMapId]) {
        groupedJobsByDropoffLocationMap[jobMapId] = {
          id: this.UUIDService.generate(),
          packages: [],
          jobIds: [],
          jobs: [],
          groupedByType: JobsGroupByEnum.dropoffLocation,
          address: job.stops[1].address,
          routedCount: !!job.routed ? 1 : 0,
          isDisableSelect: false
        };
      } else if (!!job.routed) {
        groupedJobsByDropoffLocationMap[jobMapId].routedCount++;
      }

      if (!!job.lockedRouteId) {
        groupedJobsByDropoffLocationMap[jobMapId].isDisableSelect = true;
      }

      const groupedJobsObj = groupedJobsByDropoffLocationMap[jobMapId];

      if (job.packages) {
        groupedJobsObj.packages = groupedJobsObj.packages.concat(job.packages);
      }

      groupedJobsObj.jobIds.push(job.id);
      groupedJobsObj.jobs.push(job);

      if (job.stops[0].notBefore < groupedJobsObj.notBefore || !groupedJobsObj.notBefore) {
        groupedJobsObj.notBefore = job.stops[0].notBefore;
      }
      if (job.stops[1].notAfter > groupedJobsObj.notAfter || !groupedJobsObj.notAfter) {
        groupedJobsObj.notAfter = job.stops[1].notAfter;
      }
    });

    let groupedJobs = [];

    _.forOwn(groupedJobsByDropoffLocationMap, (value: any, key) => {
      if (value.jobIds.length > 1) {
        groupedJobs.push(value);
      } else {
        const job = jobs.find((j) => j.id === value.jobIds[0]);
        groupedJobs.push(job);
      }
    });

    groupedJobs = groupedJobs.concat(fieldServiceJobs);

    return groupedJobs;
  }
}

angular
  .module('bringoz.admin.planning')
  .service('planningJobsHelperService', PlanningJobsHelperService);
